import { useEffect, useRef } from 'react';

export function EmberBackground({ className = '' }) {
  const canvasRef = useRef(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    let animId;
    let embers = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    }, { threshold: 0.05 });
    observer.observe(canvas);

    const w = () => canvas.width;
    const h = () => canvas.height;

    const mobile = window.innerWidth < 1024;
    const MAX = mobile ? 15 : 30;

    const spawnEmber = () => ({
      x: Math.random() * w(), y: h() + 5,
      size: Math.random() * 2.5 + 0.8,
      vx: (Math.random() - 0.5) * 0.8, vy: -(Math.random() * 1.5 + 0.5),
      life: 1, decay: Math.random() * 0.008 + 0.004,
      wobble: Math.random() * Math.PI * 2, ws: Math.random() * 0.03 + 0.01,
      c: Math.random() < 0.55 ? 0 : Math.random() < 0.5 ? 1 : 2
    });

    for (let i = 0; i < Math.min(12, MAX); i++) {
      const e = spawnEmber(); e.y = Math.random() * h(); e.life = Math.random();
      embers.push(e);
    }

    const COLORS = [[255, 120, 20], [218, 165, 32], [180, 20, 20]];

    let frame = 0;
    const animate = () => {
      frame++;
      if (frame % 2 !== 0) { animId = requestAnimationFrame(animate); return; }
      if (!visibleRef.current) { animId = requestAnimationFrame(animate); return; }

      ctx.clearRect(0, 0, w(), h());
      if (frame % 6 === 0 && embers.length < MAX) embers.push(spawnEmber());

      for (let i = embers.length - 1; i >= 0; i--) {
        const e = embers[i];
        e.wobble += e.ws;
        e.x += e.vx + Math.sin(e.wobble) * 0.3;
        e.y += e.vy; e.life -= e.decay;
        if (e.life <= 0 || e.y < -10) { embers.splice(i, 1); continue; }
        const [r, g, b] = COLORS[e.c];
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.size * e.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${e.life * 0.8})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(animId); observer.disconnect(); window.removeEventListener('resize', handleResize); };
  }, []);

  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} />;
}
